import React from "react";
import { useTranslation } from "react-i18next";
import { Check, Columns2, Images, ImagePlus, Loader2, Pencil, Plus, RefreshCw, ScanFace, Sparkles, Trash2, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  anyTraining, canAdopt, canChoosePicture, canDeletePhotos, canRedrawPicture, chipFor, createBlockOf, deleteFaceLockPhotos, faceLockCostLabel,
  faceLockErrorText, FACELOCK_NAME_MAX, isWorking, isUnlocked, redrawFaceLockAnimated, redrawFaceLockPicture, renameFaceLock,
  type FaceLockAvatar, type FaceLockChip, type FaceLockEligibility,
} from "@/lib/facelock";
import { FaceLockAnimatedPortrait, FaceLockPortrait } from "./FaceLockParts";
import { useFaceLockCrew } from "./useFaceLockCrew";
import NewFaceLockDialog from "./NewFaceLockDialog";
import CreateAvatarNeedsDialog from "./CreateAvatarNeedsDialog";
import AdoptDialog from "./AdoptDialog";
import ChoosePictureDialog from "./ChoosePictureDialog";
import AnimatedChooseDialog from "./AnimatedChooseDialog";
import { faceLockScanSlot } from "./scan/FaceLockScan";
import EnhanceDialog from "./enhance/EnhanceDialog";
import EnhanceCompare from "./enhance/EnhanceCompare";
import { MARK_SECTIONS, sectionCounts } from "@/shared/face-qc/marks.mjs";
import { useToolAvailability } from "@/lib/tool-availability";

// "Casting crew" on /chopshop: every avatar this account owns (picture,
// name, status), the unlock gate (website sign-up, then purchased credits,
// then the create button with its price), and the create / rename actions.
// There is no strengthen and no lock (owner decision, 2026-09-30: the FaceLock
// Scan captures the identity completely): one set of photos, one price. GET /api/web/facelock/avatars answers 404 feature_off while
// the feature is switched off: then this renders nothing at all (a failed
// first load says so, with Try again). "Create avatar" sits in the header in
// EVERY state and is always pressable (2026-10-02): unlocked, the create
// dialog; otherwise CreateAvatarNeedsDialog says what is missing. While any
// avatar trains (or an admin's Add photos runs) it asks again every 15 s.
// An admin (crew.admin) also gets Add photos on an avatar that needs them.
// The picture is ALWAYS a portrait drawn with the avatar itself (never an
// uploaded photo, never a letter): while there is none, a silhouette that says
// why ("Drawing its picture…"), and "Draw its picture again" if drawing failed.
// PEOPLE JUDGE LIKENESS (owner rule, 2026-09-30, final): "Choose its picture"
// picks among every picture its draw kept. No score is ever shown.
// A new avatar's photos come through FaceLock Scan (the dialog's scanSlot),
// like the create step inside corrido and ads. "Delete its photos" keeps the
// scan's promise ("you can ask us to delete them"), after a confirm.
// ENHANCEMENTS (owner direction 2026-10-06, improvement only): "Enhance this
// avatar" runs a FaceLock Ink Scan (tattoos and marks) and/or a FaceLock
// SharpShoot (another moment); the avatar keeps working as it is meanwhile,
// and when the result is ready the card offers the before-and-after. Nothing
// changes unless its owner keeps it, and "Go back" is always one tap.
// TWO VERSIONS (owner, 2026-10-11): while the server sends `animated_picture`,
// the card switches between the realism picture and the same portrait in the
// 3D animated look (FaceShot Animation), each with its own buttons; the
// animated one offers "Draw it again" (failed) or "Draw a new one" (ready).

// Card buttons wrap their label inside the card: a long one ("Draw a new animated
// version · 15 credits") must never run over the next card (owner, 2026-10-27).
const CARD_ACTION = "h-auto min-h-9 whitespace-normal py-1.5 leading-snug";

const CHIP_CLASS: Record<FaceLockChip, string> = {
  training: "bg-sky-500/90 text-white",
  ready: "bg-emerald-500/90 text-white",
  failed: "bg-red-500/90 text-white",
  needsPhotos: "bg-amber-500/90 text-white",
  adopting: "bg-sky-500/90 text-white",
  // STILLS FIRST: pictures work, its video files are still training.
  stillsReady: "bg-teal-500/90 text-white",
};

function StatusChip({ avatar }: { avatar: FaceLockAvatar }) {
  const { t } = useTranslation();
  const chip = chipFor(avatar);
  const icon = chip === "training" || chip === "adopting" || chip === "stillsReady" ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
    : chip === "ready" ? <Check className="h-3 w-3" aria-hidden />
    : null;
  return (
    <span
      data-testid={`facelock-chip-${avatar.id}`}
      data-chip={chip}
      className={`absolute left-1.5 top-1.5 inline-flex max-w-[calc(100%-12px)] items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold leading-tight ${CHIP_CLASS[chip]}`}
    >
      {icon}{t(`faceLock.status.${chip}`)}
    </span>
  );
}

/** The card's enhancement line: running, ready to decide, or how the last one ended; and its buttons. */
function EnhanceBlock({ avatar, onEnhance, onCompare }: { avatar: FaceLockAvatar; onEnhance: (id: string) => void; onCompare: (id: string) => void }) {
  const { t, i18n } = useTranslation();
  // Training is offline while RunPod cannot run it (lib/tool-availability.ts); back by itself.
  const { avatars: trainerOnline } = useToolAvailability();
  const e = avatar.enhance;
  if (!e || !e.available) return null;
  const outcomeKey = e.outcome ? `faceLock.enhance.outcome.${e.outcome}` : e.state && !e.running && !e.can_decide ? `faceLock.enhance.outcome.${e.state}` : "";
  const outcome = outcomeKey && i18n.exists(outcomeKey) ? t(outcomeKey) : "";
  // The parts of the body its Ink Scans have covered so far (the rest can be scanned when they are needed).
  const counts = sectionCounts(e.marks);
  const inked = MARK_SECTIONS.filter((s) => counts[s] > 0);
  return (
    <div className="grid gap-1.5" data-testid={`facelock-enhance-${avatar.id}`} data-state={e.state || ""}>
      {inked.length > 0 && (
        <p className="text-xs leading-snug text-muted-foreground" data-testid={`facelock-enhance-ink-${avatar.id}`}>
          {t("faceLock.enhance.inkOnFile", {
            list: inked.map((s) => t("faceLock.enhance.inkOnFileItem", { section: t(`faceLock.marks.section.${s}`), count: counts[s] })).join(", "),
          })}
        </p>
      )}
      {e.running && (
        <p className="flex items-start gap-1.5 text-xs leading-snug text-muted-foreground" data-testid={`facelock-enhance-running-${avatar.id}`}>
          <Loader2 className="mt-0.5 h-3 w-3 shrink-0 animate-spin" aria-hidden /> {t(`faceLock.enhance.running.${e.state || "checking"}`)}
        </p>
      )}
      {e.can_decide && (
        <Button type="button" size="sm" className={CARD_ACTION} data-testid={`facelock-enhance-decide-${avatar.id}`} onClick={() => onCompare(avatar.id)}>
          <Columns2 aria-hidden /> {t("faceLock.enhance.decide")}
        </Button>
      )}
      {!e.running && !e.can_decide && outcome && (
        <p className="text-xs leading-snug text-muted-foreground" data-testid={`facelock-enhance-outcome-${avatar.id}`}>{outcome}</p>
      )}
      {e.can_rollback && (
        <button type="button" data-testid={`facelock-enhance-review-${avatar.id}`} onClick={() => onCompare(avatar.id)}
          className="inline-flex w-fit items-center gap-1 rounded px-1 py-0.5 text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline">
          <Columns2 className="h-3.5 w-3.5" aria-hidden /> {t("faceLock.enhance.review")}
        </button>
      )}
      {e.can_start && (
        <Button type="button" size="sm" className={CARD_ACTION} variant="outline" data-testid={`facelock-enhance-start-${avatar.id}`} disabled={!trainerOnline} onClick={() => onEnhance(avatar.id)}>
          <Sparkles aria-hidden /> {t("faceLock.enhance.button")}
        </Button>
      )}
    </div>
  );
}

function AvatarCard({ avatar, adoptable, onAdopt, onRenamed, onRedrawn, onChoosePicture, onChooseAnimated, onPhotosDeleted, onEnhance, onCompare }: {
  avatar: FaceLockAvatar;
  /** An admin, and this avatar needs its photos: offer Add photos. */
  adoptable: boolean;
  onAdopt: (id: string) => void;
  onRenamed: () => void;
  onRedrawn: () => void;
  onChoosePicture: (id: string) => void;
  /** "Choose its animated picture": among the versions of its animated draw. */
  onChooseAnimated: (id: string) => void;
  onPhotosDeleted: () => void;
  onEnhance: (id: string) => void;
  onCompare: (id: string) => void;
}) {
  const { t, i18n } = useTranslation();
  // Pictures are drawn on the music-video worker; offline with it, back by itself.
  const { music: picturesOnline } = useToolAvailability();
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(avatar.name);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [redrawing, setRedrawing] = React.useState(false);
  const [redrawError, setRedrawError] = React.useState("");
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const [version, setVersion] = React.useState<"real" | "animated">("real");
  const [animRedrawing, setAnimRedrawing] = React.useState(false);
  const [animRedrawError, setAnimRedrawError] = React.useState("");
  const animated = avatar.animated_picture;
  const showAnimated = !!animated && version === "animated";
  const [deleting, setDeleting] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState("");
  const training = isWorking(avatar);
  const msgKey = avatar.message_key ? `faceLock.messages.${avatar.message_key}` : "";
  const message = msgKey && i18n.exists(msgKey) ? t(msgKey) : "";
  const trimmed = draft.trim();
  const draftOk = trimmed.length >= 1 && trimmed.length <= FACELOCK_NAME_MAX;

  const save = async () => {
    if (!draftOk || saving) return;
    if (trimmed === avatar.name) { setEditing(false); return; }
    setSaving(true);
    setError("");
    const res = await renameFaceLock(avatar.id, trimmed);
    setSaving(false);
    if (res.ok === false) { setError(faceLockErrorText(t, res)); return; }
    setEditing(false);
    onRenamed();
  };

  const redraw = async () => {
    if (redrawing) return;
    setRedrawing(true);
    setRedrawError("");
    const res = await redrawFaceLockPicture(avatar.id);
    setRedrawing(false);
    if (res.ok === false) { setRedrawError(t("faceLock.picture.redrawFailed")); return; }
    onRedrawn();
  };

  const redrawAnimated = async () => {
    if (animRedrawing) return;
    setAnimRedrawing(true);
    setAnimRedrawError("");
    const res = await redrawFaceLockAnimated(avatar.id);
    setAnimRedrawing(false);
    if (res.ok === false) {
      setAnimRedrawError(res.error === "insufficient_credits"
        ? t("faceLock.animated.needCredits", { count: animated?.redraw_credits || 0 })
        : t("faceLock.animated.redrawFailed"));
      return;
    }
    onRedrawn();
  };

  const deletePhotos = async () => {
    if (deleting) return;
    setDeleting(true);
    setDeleteError("");
    const res = await deleteFaceLockPhotos(avatar.id);
    setDeleting(false);
    if (res.ok === false) {
      setDeleteError(res.error === "avatar_busy" ? faceLockErrorText(t, res) : t("faceLock.deletePhotos.failed"));
      return;
    }
    setConfirmDelete(false);
    onPhotosDeleted();
  };

  return (
    <article data-testid={`facelock-card-${avatar.id}`} className="grid min-w-0 content-start gap-2 rounded-lg border border-border bg-background/40 p-2">
      {animated && (
        <div role="tablist" aria-label={t("faceLock.versions.label")} data-testid={`facelock-versions-${avatar.id}`}
          className="grid grid-cols-2 gap-0.5 rounded-md bg-muted p-0.5 text-xs font-semibold">
          {(["real", "animated"] as const).map((v) => (
            <button
              key={v}
              type="button"
              role="tab"
              aria-selected={version === v}
              data-testid={`facelock-version-${v}-${avatar.id}`}
              onClick={() => setVersion(v)}
              className={`rounded px-1.5 py-1 transition-colors ${version === v ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
            >
              {t(v === "real" ? "faceLock.versions.realism" : "faceLock.versions.animated")}
            </button>
          ))}
        </div>
      )}
      <div className="relative aspect-[3/4] overflow-hidden rounded-md bg-muted">
        {showAnimated ? <FaceLockAnimatedPortrait avatar={avatar} /> : <FaceLockPortrait avatar={avatar} />}
        <StatusChip avatar={avatar} />
      </div>

      {editing ? (
        <div className="grid gap-1.5">
          <Input
            value={draft}
            maxLength={FACELOCK_NAME_MAX}
            aria-label={t("faceLock.renameLabel", { name: avatar.name })}
            data-testid={`facelock-rename-input-${avatar.id}`}
            disabled={saving}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") void save(); if (e.key === "Escape") setEditing(false); }}
            className="h-9"
          />
          <div className="flex gap-1.5">
            <Button type="button" size="sm" data-testid={`facelock-rename-save-${avatar.id}`} disabled={!draftOk || saving} onClick={() => void save()}>
              {saving ? t("faceLock.saving") : t("faceLock.save")}
            </Button>
            <Button type="button" size="sm" variant="ghost" disabled={saving} onClick={() => { setEditing(false); setError(""); }}>
              {t("faceLock.cancel")}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-1">
          <h3 className="min-w-0 truncate text-sm font-semibold" data-testid={`facelock-name-${avatar.id}`}>{avatar.name}</h3>
          <button
            type="button"
            data-testid={`facelock-rename-${avatar.id}`}
            aria-label={t("faceLock.rename")}
            title={t("faceLock.rename")}
            onClick={() => { setDraft(avatar.name); setEditing(true); setError(""); }}
            className="shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <Pencil className="h-3.5 w-3.5" aria-hidden />
          </button>
        </div>
      )}
      {error && <div role="alert" className="text-xs text-red-500">{error}</div>}

      <div className="text-xs text-muted-foreground">{t("faceLock.photoCount", { count: avatar.photo_count })}</div>

      {training && avatar.progress !== null && (
        <div className="grid gap-1" data-testid={`facelock-progress-${avatar.id}`}>
          <div className="h-1.5 overflow-hidden rounded-full bg-muted">
            <div className="h-full bg-primary" style={{ width: `${Math.max(0, Math.min(100, avatar.progress))}%` }} />
          </div>
          <div className="text-[11px] text-muted-foreground">{t("faceLock.progress", { progress: Math.round(avatar.progress) })}</div>
        </div>
      )}
      {message && <p data-testid={`facelock-message-${avatar.id}`} className="text-xs leading-snug text-muted-foreground">{message}</p>}

      {showAnimated && animated && animated.can_redraw && (
        <Button
          type="button"
          size="sm" className={CARD_ACTION}
          variant="outline"
          data-testid={`facelock-animated-redraw-${avatar.id}`}
          disabled={animRedrawing || !picturesOnline}
          onClick={() => void redrawAnimated()}
        >
          <RefreshCw aria-hidden /> {animRedrawing ? t("faceLock.animated.redrawing")
            : animated.state === "failed" ? t("faceLock.animated.redraw")
              : animated.redraw_credits > 0 ? t("faceLock.animated.redrawNewPrice", { count: animated.redraw_credits })
                : t("faceLock.animated.redrawNew")}
        </Button>
      )}
      {showAnimated && animated && animated.url && animated.state !== "drawing" && animated.choices.length >= 2 && (
        <Button
          type="button"
          size="sm" className={CARD_ACTION}
          variant="outline"
          data-testid={`facelock-animated-choose-${avatar.id}`}
          onClick={() => onChooseAnimated(avatar.id)}
        >
          <Images aria-hidden /> {t("faceLock.animated.choose")}
        </Button>
      )}
      {showAnimated && animRedrawError && (
        <div role="alert" data-testid={`facelock-animated-redraw-error-${avatar.id}`} className="text-xs text-red-500">{animRedrawError}</div>
      )}

      {!showAnimated && canRedrawPicture(avatar) && (
        <Button
          type="button"
          size="sm" className={CARD_ACTION}
          variant="outline"
          data-testid={`facelock-redraw-${avatar.id}`}
          disabled={redrawing || !picturesOnline}
          onClick={() => void redraw()}
        >
          <RefreshCw aria-hidden /> {redrawing ? t("faceLock.picture.redrawing") : t("faceLock.picture.redraw")}
        </Button>
      )}
      {!showAnimated && redrawError && <div role="alert" data-testid={`facelock-redraw-error-${avatar.id}`} className="text-xs text-red-500">{redrawError}</div>}

      {!showAnimated && canChoosePicture(avatar) && (
        <Button
          type="button"
          size="sm" className={CARD_ACTION}
          variant="outline"
          data-testid={`facelock-choose-picture-${avatar.id}`}
          onClick={() => onChoosePicture(avatar.id)}
        >
          <Images aria-hidden /> {t("faceLock.choosePicture.button")}
        </Button>
      )}

      <EnhanceBlock avatar={avatar} onEnhance={onEnhance} onCompare={onCompare} />

      {adoptable && (
        <Button
          type="button"
          size="sm" className={CARD_ACTION}
          data-testid={`facelock-adopt-${avatar.id}`}
          onClick={() => onAdopt(avatar.id)}
        >
          <ImagePlus aria-hidden /> {t("faceLock.adopt.button")}
        </Button>
      )}

      {canDeletePhotos(avatar) && !confirmDelete && (
        <button
          type="button"
          data-testid={`facelock-delete-photos-${avatar.id}`}
          onClick={() => { setConfirmDelete(true); setDeleteError(""); }}
          className="inline-flex w-fit items-center gap-1 rounded px-1 py-0.5 text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
        >
          <Trash2 className="h-3.5 w-3.5" aria-hidden /> {t("faceLock.deletePhotos.button")}
        </button>
      )}
      {confirmDelete && (
        <div role="alertdialog" aria-labelledby={`facelock-delete-photos-q-${avatar.id}`} data-testid={`facelock-delete-photos-confirm-${avatar.id}`}
          className="grid gap-1.5 rounded-md border border-border bg-muted/40 p-2">
          <p id={`facelock-delete-photos-q-${avatar.id}`} className="text-xs leading-snug">{t("faceLock.deletePhotos.confirm")}</p>
          <div className="flex flex-wrap gap-1.5">
            <Button type="button" size="sm" variant="destructive" disabled={deleting} data-testid={`facelock-delete-photos-yes-${avatar.id}`}
              onClick={() => void deletePhotos()}>
              {deleting ? t("faceLock.deletePhotos.deleting") : t("faceLock.deletePhotos.yes")}
            </Button>
            <Button type="button" size="sm" variant="ghost" disabled={deleting} onClick={() => { setConfirmDelete(false); setDeleteError(""); }}>
              {t("faceLock.deletePhotos.no")}
            </Button>
          </div>
        </div>
      )}
      {deleteError && <div role="alert" data-testid={`facelock-delete-photos-error-${avatar.id}`} className="text-xs text-red-500">{deleteError}</div>}
    </article>
  );
}

/** Sign up -> purchased credits -> create. Shown until both conditions hold. */
function GatePanel({ eligibility, busy }: { eligibility: FaceLockEligibility; busy: boolean }) {
  const { t } = useTranslation();
  const e = eligibility;
  // An admin account is not held to the purchase threshold.
  const boughtOk = e.purchased_credits >= e.required_purchased || e.gate_waived === true;
  const missing = Math.max(0, Math.ceil(e.required_purchased - e.purchased_credits));
  const pct = e.required_purchased > 0 ? Math.min(100, Math.round((e.purchased_credits / e.required_purchased) * 100)) : 100;
  const stepClass = (done: boolean) => `flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${done ? "bg-emerald-500 text-white" : "bg-muted text-muted-foreground"}`;
  return (
    <div data-testid="facelock-gate" className="mb-4 grid gap-3 rounded-lg border border-border bg-muted/30 p-3">
      <div>
        <div className="font-semibold">{t("faceLock.gate.title")}</div>
        <p className="text-sm text-muted-foreground">{t("faceLock.gate.body", { required: e.required_purchased })}</p>
      </div>
      <ol className="grid gap-3">
        <li data-testid="facelock-gate-signup" data-done={e.signed_up ? "true" : "false"} className="flex gap-2">
          <span className={stepClass(e.signed_up)}>{e.signed_up ? <Check className="h-3.5 w-3.5" aria-hidden /> : 1}</span>
          <div className="grid gap-0.5 text-sm">
            <span className="font-medium">{t("faceLock.gate.signUp")}</span>
            {!e.signed_up && <span className="text-muted-foreground">{t("faceLock.gate.signUpTodo")}</span>}
          </div>
        </li>
        <li data-testid="facelock-gate-credits" data-done={boughtOk ? "true" : "false"} className="flex gap-2">
          <span className={stepClass(boughtOk)}>{boughtOk ? <Check className="h-3.5 w-3.5" aria-hidden /> : 2}</span>
          <div className="grid flex-1 gap-1 text-sm">
            <span className="font-medium" data-testid="facelock-gate-bought">{t("faceLock.gate.bought", { n: e.purchased_credits, required: e.required_purchased })}</span>
            <div className="h-1.5 max-w-xs overflow-hidden rounded-full bg-muted">
              <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
            </div>
            {!boughtOk && (
              <span className="text-muted-foreground">
                {t("faceLock.gate.boughtTodo", { count: missing })}{" "}
                <a href="/pricing" data-testid="facelock-gate-buy" className="font-semibold text-primary underline">{t("faceLock.gate.buyCredits")}</a>
              </span>
            )}
          </div>
        </li>
        {/* No button here: the one Create avatar button is in the section's
            header, always pressable (it says what is missing). */}
        <li className="flex gap-2">
          <span className={stepClass(false)}>3</span>
          <div className="grid gap-1.5 text-sm">
            <span className="font-medium">{t("faceLock.gate.create")}</span>
            {busy && <span className="text-muted-foreground">{t("faceLock.gate.busy")}</span>}
          </div>
        </li>
      </ol>
    </div>
  );
}

type CastingCrewProps = {
  /** A Telegram-studio account (not signed up on the website): take it to the Add your email form. */
  onAddEmail?: () => void;
};

const SECTION_CLASS = "mb-8 scroll-mt-20 rounded-lg bg-card p-4";

export default function CastingCrew({ onAddEmail }: CastingCrewProps = {}) {
  const { t } = useTranslation();
  const { status, crew, refresh } = useFaceLockCrew(true, { poll: true, animated: true });
  const { avatars: trainerOnline } = useToolAvailability();
  const [creating, setCreating] = React.useState(false);
  const [needsOpen, setNeedsOpen] = React.useState(false);
  const [adoptId, setAdoptId] = React.useState<string | null>(null);
  const [pictureId, setPictureId] = React.useState<string | null>(null);
  const [animChooseId, setAnimChooseId] = React.useState<string | null>(null);
  const [enhanceId, setEnhanceId] = React.useState<string | null>(null);
  const [compareId, setCompareId] = React.useState<string | null>(null);
  // /chopshop#casting-crew (the navbar's and the landing page's Avatars
  // entry) is landed by ChopShopPage (lib/hash-landing.ts): it waits for this
  // section, keeps it lined up while the page fills in, and leaves Back,
  // Forward and a reload to ScrollManager. This component never scrolls the
  // page itself (review 2026-10-03: its own scroll fought those).

  // A first load that failed (5xx, network): say so, with a retry, instead of an empty page.
  if (status === "error" && !crew) {
    return (
      <section id="casting-crew" aria-labelledby="casting-crew-title" data-testid="casting-crew" className={SECTION_CLASS}>
        <h2 id="casting-crew-title" className="flex items-center gap-2 text-xl font-semibold">
          <ScanFace className="h-5 w-5" aria-hidden /> {t("faceLock.title")}
        </h2>
        <div role="alert" data-testid="facelock-load-failed" className="mt-2 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
          {t("faceLock.loadFailed")}
          <Button type="button" size="sm" variant="outline" data-testid="facelock-retry" onClick={() => void refresh()}>
            <RefreshCw aria-hidden /> {t("faceLock.retry")}
          </Button>
        </div>
      </section>
    );
  }
  if (status !== "on" || !crew) return null;

  const e = crew.eligibility;
  const avatars = [...crew.avatars].sort((a, b) => (Date.parse(a.created_at) || 0) - (Date.parse(b.created_at) || 0));
  const unlocked = isUnlocked(e);
  const training = anyTraining(avatars);
  const block = createBlockOf(e, avatars);
  const adoptTarget = adoptId ? avatars.find((a) => a.id === adoptId) || null : null;
  const pictureTarget = pictureId ? avatars.find((a) => a.id === pictureId) || null : null;
  const enhanceTarget = enhanceId ? avatars.find((a) => a.id === enhanceId) || null : null;
  const compareTarget = compareId ? avatars.find((a) => a.id === compareId) || null : null;

  return (
    <section id="casting-crew" aria-labelledby="casting-crew-title" data-testid="casting-crew" className={SECTION_CLASS}>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1 basis-64">
          <h2 id="casting-crew-title" className="flex items-center gap-2 text-xl font-semibold">
            <ScanFace className="h-5 w-5" aria-hidden /> {t("faceLock.title")}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">{t("faceLock.subtitle")}</p>
        </div>
        {/* ALWAYS pressable (owner, 2026-10-02: "there is no create avatar
            button anywhere"). Unlocked and free to create: the create dialog.
            Otherwise it says exactly why not (CreateAvatarNeedsDialog). */}
        <Button
          type="button"
          size="lg"
          data-testid="facelock-new"
          data-block={block || ""}
          disabled={!trainerOnline}
          onClick={() => (block ? setNeedsOpen(true) : setCreating(true))}
          className="shrink-0"
        >
          <Plus aria-hidden /> {unlocked
            ? t("faceLock.createButton", { cost: faceLockCostLabel(t, e.next_cost) })
            : t("faceLock.createButtonPlain")}
        </Button>
      </div>

      {/* RunPod cannot run right now: nothing here can be trained or drawn. Lifts by itself. */}
      {!trainerOnline && (
        <p data-testid="facelock-offline" role="note" className="mb-3 flex items-start gap-2 rounded-xl border border-white/10 bg-white/[0.04] p-3 text-sm text-muted-foreground">
          <Wrench className="mt-0.5 h-4 w-4 shrink-0" aria-hidden /> {t("offline.long")}
        </p>
      )}

      {unlocked && !e.can_create && (
        <p data-testid="facelock-create-blocked" className="mb-3 text-sm text-muted-foreground">
          {training ? t("faceLock.gate.busy") : t("faceLock.gate.closed")}
        </p>
      )}
      {!unlocked && <GatePanel eligibility={e} busy={training} />}

      {avatars.length === 0 ? (
        <p data-testid="facelock-empty" className="text-sm text-muted-foreground">{t("faceLock.empty")}</p>
      ) : (
        <div data-testid="facelock-grid" className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {avatars.map((a) => (
            <AvatarCard
              key={a.id}
              avatar={a}
              adoptable={canAdopt(a, crew)}
              onAdopt={setAdoptId}
              onRenamed={() => void refresh()}
              onRedrawn={() => void refresh()}
              onChoosePicture={setPictureId}
              onChooseAnimated={setAnimChooseId}
              onPhotosDeleted={() => void refresh()}
              onEnhance={setEnhanceId}
              onCompare={setCompareId}
            />
          ))}
        </div>
      )}
      {training && <p className="mt-3 text-xs text-muted-foreground">{t("faceLock.trainingNote")}</p>}

      <NewFaceLockDialog open={creating} onOpenChange={setCreating} eligibility={e} onCreated={() => void refresh()}
        scanSlot={faceLockScanSlot} />
      <CreateAvatarNeedsDialog open={needsOpen} onOpenChange={setNeedsOpen} block={block} eligibility={e} onAddEmail={onAddEmail} />
      <ChoosePictureDialog
        avatar={pictureTarget}
        open={!!pictureTarget}
        onOpenChange={(o) => { if (!o) setPictureId(null); }}
        onChanged={() => void refresh()}
      />
      <AnimatedChooseDialog
        avatar={avatars.find((x) => x.id === animChooseId) || null}
        open={!!animChooseId}
        onOpenChange={(o) => { if (!o) setAnimChooseId(null); }}
        onChanged={() => void refresh()}
      />
      <EnhanceDialog
        avatar={enhanceTarget}
        open={!!enhanceTarget}
        onOpenChange={(o) => { if (!o) setEnhanceId(null); }}
        onStarted={() => void refresh()}
      />
      <EnhanceCompare
        avatar={compareTarget}
        open={!!compareTarget}
        onOpenChange={(o) => { if (!o) setCompareId(null); }}
        onChanged={() => void refresh()}
      />
      {crew.admin && (
        <AdoptDialog
          avatar={adoptTarget}
          open={!!adoptTarget}
          onOpenChange={(o) => { if (!o) setAdoptId(null); }}
          onStarted={() => void refresh()}
        />
      )}
    </section>
  );
}
